import React from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Fieldset } from 'primereact/fieldset';
import { Badge } from 'primereact/badge';
import { Divider } from 'primereact/divider';
import { Tooltip } from 'primereact/tooltip';

const ProjectDetailsPanel = ({ project, onClose }) => {
  if (!project) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-CO');
  };

  const renderField = (label, value, icon = null) => {
    if (!value) return null;
    return (
      <div className="flex align-items-center mb-2">
        {icon && <i className={`${icon} mr-2 text-primary`}></i>}
        <span className="font-medium text-900" style={{ minWidth: '120px', marginRight: '8px' }}>{label}:</span>
        <span className="text-700">{value}</span>
      </div>
    );
  };

  const renderFieldWithTooltip = (label, value, icon = null, tooltipText = null) => {
    if (!value) return null;
    const tooltipId = `tooltip-${label.replace(/\s+/g, '-').toLowerCase()}`;
    return (
      <div className="flex align-items-center mb-2">
        {icon && <i className={`${icon} mr-2 text-primary`}></i>}
        <span className="font-medium text-900" style={{ minWidth: '120px', marginRight: '8px' }}>{label}:</span>
        <span className="text-700">{value}</span>
        {tooltipText && (
          <>
            <i 
              id={tooltipId}
              className="pi pi-info-circle ml-2" 
              style={{ fontSize: '12px', color: '#666', cursor: 'pointer' }}
            ></i>
            <Tooltip target={`#${tooltipId}`} content={tooltipText} position="top" />
          </>
        )}
      </div>
    );
  };

  const headerTemplate = (
    <div className="flex justify-content-between align-items-center" style={{ padding: '16px' }}>
      <span className="text-xl font-bold">{project.name}</span>
      <Button
        icon="pi pi-times"
        rounded
        text
        severity="secondary"
        onClick={onClose}
        tooltip="Cerrar panel"
      />
    </div>
  );

  return (
    <Card
      header={headerTemplate}
      className="w-full border-none shadow-none"
      style={{
        height: '100%',
        borderRadius: 0,
        border: 'none',
        boxShadow: 'none'
      }}
    >
      {/* Información Básica */}
      <Fieldset legend="Información Básica" toggleable className="mb-3">
        {renderField('Departamento', project.departamento, 'pi pi-map-marker')}
        {renderField('Ubicación', project.location, 'pi pi-map')}
        {renderField('Área del proyecto', project.hectares ? `${Number(project.hectares).toLocaleString('es-CO')} ha` : null, 'pi pi-chart-line')}
        <div className="flex align-items-center mb-2">
          <i className="pi pi-info-circle mr-2 text-primary"></i>
          <span className="font-medium text-900" style={{ minWidth: '120px' }}>Estado:</span>
          <span className="text-700" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
            {project.status?.name || 'Sin estado'}
          </span>
        </div>
        {project.description && (
          <div className="mb-2">
            <span className="font-medium text-900 block mb-1">Descripción:</span>
            <p className="text-700 m-0 text-sm">{project.description}</p>
          </div>
        )}
      </Fieldset>

      {/* Fechas */}
      <Fieldset legend="Fechas" toggleable collapsed className="mb-3">
        {renderFieldWithTooltip('Fecha Registro', formatDate(project.registrationDate), 'pi pi-calendar', 'Fecha en que el proyecto fue registrado en el sistema')}
        {renderFieldWithTooltip('Fecha Inicio', formatDate(project.startDate), 'pi pi-play-circle', 'Fecha de inicio segun pagina web certificadora')}
        {renderFieldWithTooltip('Fecha Fin', formatDate(project.endDate), 'pi pi-stop-circle', 'Fecha de finalización segun pagina web certificadora')}
        {renderFieldWithTooltip('PDD Inicio', formatDate(project.pddStartDate), 'pi pi-clock', 'Fecha de inicio reportada en PDD')}
        {renderFieldWithTooltip('PDD Fin', formatDate(project.pddEndDate), 'pi pi-clock', 'Fecha de finalización eportada en PDD')}
        {renderFieldWithTooltip('Fecha Consulta', formatDate(project.queryDate), 'pi pi-search', 'Fecha de la última consulta o actualización de datos')}
      </Fieldset>

      {/* Organizaciones */}
      <Fieldset legend="Organizaciones" toggleable collapsed className="mb-3">
        {renderField('Comunidades', project.communitiesInvolved, 'pi pi-users')}
        {renderField('Empresa desarrolladoras', project.developerOrganizations, 'pi pi-building')}
        {renderField('Org. Colaboradoras', project.collaboratingOrganizations, 'pi pi-share-alt')}
        {renderField('Validador', project.validator?.name, 'pi pi-check-circle')}
        {renderField('Certificadora', project.certifier?.name, 'pi pi-verified')}
      </Fieldset>

      {/* Enlaces */}
      {project.link && (
        <Fieldset legend="Enlaces" toggleable collapsed className="mb-3">
          <div className="flex align-items-center">
            <i className="pi pi-external-link mr-2 text-primary"></i>
            <a
              href={project.link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:text-primary-600 no-underline"
            >
              {project.link}
            </a>
          </div>
        </Fieldset>
      )}

      <Divider />

      <div className="flex justify-content-end">
        <Button
          label="Cerrar"
          icon="pi pi-times"
          onClick={onClose}
          className="p-button-secondary"
        />
      </div>
    </Card>
  );
};

export default ProjectDetailsPanel;